import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import SearchInput from "../../../../../../../components/SearchInput";
import CustomTabs from "../../../../../../../components/app/CustomTabs";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { roleFilter } from "../../../../../../../utils/utils";
import { DEFAULT_SUPER_BILL_FILTERS } from "../../../../billings/BillingsLayout";
import SuperBillFilter, {
  SuperBillFilterData,
} from "../../../../billings/SuperBillFilter";
import UpsertPatientPayments from "../../../../billings/payments/UpsertPatientPayments";
import AddCardModal from "./AddCardModal";
import PatientBills from "./PatientBills";
import PatientCardDetails from "./PatientCardDetails";
import PatientClaims from "./PatientClaims";
import PatientEraEob from "./PatientEraEob";
import PatientInsurancePayment from "./PatientInsurancePayment";
import PatientLedger from "./PatientLedger";
import PatientPaymentsNew from "./PatientPaymentsNew";
import PatientReadyForBilling from "./PatientReadyForBilling";
import {
  DEFAULT_PATIENT_CLAIMS_FILTERS,
  PatientClaimsFilterData,
} from "./financial-filters-context";
import { PatientClaimsFilters } from "./PatientClaimsFilters";

const ActiveContentComponent: Record<string, ReactNode> = {
  "": <PatientBills />,
  "ready-for-billing": <PatientReadyForBilling />,
  claims: <PatientClaims />,
  "era-eob": <PatientEraEob />,
  payments: <PatientPaymentsNew />,
  "insurance-payment": <PatientInsurancePayment />,
  carddetails: <PatientCardDetails />,
  ledger: <PatientLedger />,
};

const FinancialLayout = () => {
  const [paymentsDisclosure, addCardDisclosure] = useReduceDisclosure(2);
  const patientData: any = useOutletContext();
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  // When the Collect Payment drawer closes, refetch the Payment History table
  // and the Credit/Outstanding balance summary (/v2/by-visit) so a freshly
  // collected payment (e.g. an Advance) is reflected in the balance tags.
  const queryClient = useQueryClient();
  const wasPaymentsOpenRef = useRef(false);
  useEffect(() => {
    if (wasPaymentsOpenRef.current && !paymentsDisclosure.opened) {
      queryClient.invalidateQueries({
        queryKey: ["get-patient-payment-summary"],
      });
      queryClient.invalidateQueries({ queryKey: ["get-patient-payment-v2"] });
    }
    wasPaymentsOpenRef.current = paymentsDisclosure.opened;
  }, [paymentsDisclosure.opened, queryClient]);

  const navigate = useNavigate();
  const defTabPath = location.pathname.split("/").pop();
  const defTab =
    defTabPath === "financial" || isVisitNotePage ? "" : (defTabPath as string);
  const [activeButton, setActiveButton] = useState<string>(defTab);

  useEffect(() => {
    setActiveButton(defTab);
  }, [defTab]);

  const [invoiceFilter, setInvoiceFilter] = useState<SuperBillFilterData>(
    DEFAULT_SUPER_BILL_FILTERS
  );
  const [invoiceSearchKey, setInvoiceSearchKey, instantInvoiceSearchKey] =
    useDebounce<string>("");

  const [claimsFilter, setClaimsFilter] = useState<PatientClaimsFilterData>(
    DEFAULT_PATIENT_CLAIMS_FILTERS
  );
  const [claimsSearchKey, setClaimsSearchKey, instantClaimsSearchKey] =
    useDebounce<string>("");

  const FILTER_BARS: Record<string, ReactNode> = {
    "": (
      <Flex alignItems={"center"} gap={"10px"} flexWrap={"wrap"}>
        <Box width={"15rem"}>
          <SearchInput
            searchKey={instantInvoiceSearchKey}
            onSearch={setInvoiceSearchKey}
            placeholder="Search by Provider Name"
            name="invoiceSearch"
          />
        </Box>
        <SuperBillFilter
          filters={invoiceFilter}
          setSuperBillFilters={setInvoiceFilter}
        />
      </Flex>
    ),
    claims: (
      <Flex alignItems={"center"} gap={"10px"} flexWrap={"wrap"}>
        <Box width={"15rem"}>
          <SearchInput
            searchKey={instantClaimsSearchKey}
            onSearch={setClaimsSearchKey}
            placeholder="Search by Provider Name"
            name="claimsSearch"
          />
        </Box>
        <PatientClaimsFilters
          filters={claimsFilter}
          setFilters={setClaimsFilter}
        />
      </Flex>
    ),
  };

  const endContentMap: Record<string, ReactNode> = {
    "": FILTER_BARS[""],
    claims: FILTER_BARS["claims"],
    payments: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => paymentsDisclosure.open()}
      >
        Collect Payment
      </ChartActionStrip.Button>
    ),
    carddetails: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => addCardDisclosure.open()}
      >
        Add Card Details
      </ChartActionStrip.Button>
    ),
  };

  const { hasView: hasViewBilling } = useRoleController("Billing");
  const { hasView: hasViewCreditCard } = useRoleController("Credit card");

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {paymentsDisclosure.opened && (
        <UpsertPatientPayments
          title="Patient Payment Entry"
          disclosureHost={paymentsDisclosure}
        />
      )}
      <AddCardModal disclosureHost={addCardDisclosure} />

      <ChartActionStrip
        startContent={
          <CustomTabs
            tabsData={roleFilter({
              list: TABS_DATA,
              compWith: {
                "": hasViewBilling,
                carddetails: hasViewCreditCard,
              },
            })}
            value={activeButton}
            onChange={(v) => {
              setActiveButton(v);
              if (!isVisitNotePage) navigate(v);
            }}
          />
        }
        endContent={!isVisitNotePage ? endContentMap[activeButton] : undefined}
      />

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isVisitNotePage ? (
          ActiveContentComponent[activeButton]
        ) : (
          <Outlet
            context={{
              ...patientData,
              invoiceSearchKey,
              invoiceFilter,
              claimsSearchKey,
              claimsFilter,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default FinancialLayout;

const TABS_DATA = [
  {
    value: "",
    label: "Invoices",
  },
  // {
  //   value: "ready-for-billing",
  //   label: "Ready For Billing",
  // },
  {
    value: "claims",
    label: "Claims",
  },
  // {
  //   value: "era-eob",
  //   label: "ERA/EOB",
  // },
  {
    value: "payments",
    label: "Payment History",
  },
  // {
  //   value: "insurance-payment",
  //   label: "Insurance Payment",
  // },
  // {
  //   value: "patientbalance",
  //   label: "Patient Balance",
  // },
  {
    value: "carddetails",
    label: "Card Details",
  },
  // ECRH-5330: Ledger tab hidden from Financials section
  // {
  //   value: "ledger",
  //   label: "Ledger",
  // },
];
